import React, { useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { ACTIVITY_TYPES } from "Constants/constants";
import useDecodedParams from "Hooks/useDecodedParams";
import getUrl from "Libs/getUrl";
import { getLogUrl as getLogUrlUtil } from "Reducers/activity/activity.hooks";
import LogModal from "src/organization/common/components/ActivityList/LogModal/LogModal";

import ActivityItem from "../activities/ActivityItem";

import ActivityEmpty from "./ActivityEmpty";
import * as S from "./ActivityList.styles";

import type { Activity } from "@packages/client";

type ActivityListProps = {
  activities: Activity[];
  activityType: "all" | "pending" | "running" | "staged";
  canEditProject?: boolean;
  organizationId: string;
  activityIsLoading: boolean;
  openedCancelModal?: string;
  setOpenedCancelModal?: React.Dispatch<React.SetStateAction<string>>;
  shouldShowLog?: boolean;
  setSelectedFailedActivityLog?: React.Dispatch<React.SetStateAction<string>>;
  selectedFailedActivityLog?: string;
  shouldForceLoader?: boolean;
};
const sortScheduledActivities = activities =>
  activities.sort(a =>
    a.type === ACTIVITY_TYPES.MAINTENANCE.UPGRADE ? 1 : -1
  );

const sortByCreationDate = (item1, item2) =>
  new Date(item2.created_at).getTime() - new Date(item1.created_at).getTime();

const ActivityList = ({
  activities,
  activityType,
  activityIsLoading,
  canEditProject,
  organizationId,
  openedCancelModal,
  setOpenedCancelModal,
  shouldShowLog,
  setSelectedFailedActivityLog,
  selectedFailedActivityLog,
  shouldForceLoader = false
}: ActivityListProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openLog, setOpenLog] = useState<Activity | undefined>();

  const { environmentId, projectId, integrationId } = useDecodedParams<{
    environmentId: string;
    projectId: string;
    integrationId: string;
  }>();

  const activityList = useMemo(
    () =>
      activityType === "pending"
        ? sortScheduledActivities(activities.sort(sortByCreationDate))
        : activities.sort(sortByCreationDate),
    [activities, activityType]
  );
  const shouldShowEmptySection = useMemo(
    () => activityList?.length === 0 && !activityIsLoading,
    [activityList, activityIsLoading]
  );
  const getLogUrl = (activity: Activity) => {
    const props = {
      organizationId,
      projectId,
      environmentId: environmentId ?? "",
      integrationId: integrationId ?? "",
      activityId: activity.id
    };
    const backupsHistoryUrl = getUrl({
      key: "organization.project.environment.backups.history",
      props
    });
    if (location.pathname === backupsHistoryUrl) {
      setOpenLog(activity);
    } else {
      const logUrl = getLogUrlUtil({ activity, ...props });
      logUrl && navigate(logUrl);
    }
  };

  return (
    <S.ActivityListLayout>
      {openLog && (
        <LogModal
          initialActivity={openLog}
          closeModal={() => {
            setOpenLog(undefined);
          }}
        />
      )}
      <S.ActivityListScroll>
        {shouldShowEmptySection ? (
          <ActivityEmpty activityType={activityType} />
        ) : (
          <S.ListContainer>
            {activityList.map(activity => {
              return (
                <ActivityItem
                  shouldForceLoader={shouldForceLoader}
                  key={activity.id}
                  openModal={() => {
                    getLogUrl(activity);
                  }}
                  activity={activity}
                  canEditProject={canEditProject}
                  openedCancelModal={openedCancelModal}
                  setOpenedCancelModal={setOpenedCancelModal}
                  shouldShowLog={shouldShowLog}
                  setSelectedFailedActivityLog={setSelectedFailedActivityLog}
                  selectedFailedActivityLog={selectedFailedActivityLog}
                />
              );
            })}
          </S.ListContainer>
        )}
        {activityIsLoading && <S.Loading />}
      </S.ActivityListScroll>
    </S.ActivityListLayout>
  );
};

export default ActivityList;
